import React, { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    LabelList,
    ResponsiveContainer,
} from "recharts";
import { getAiListingAnalytics } from "../../api/context/analytics";
import {
    FiInfo,
    FiRefreshCw,
    FiDollarSign,
    FiUsers,
    FiPackage,
    FiTrendingUp,
    FiCreditCard,
    FiZap,
    FiArrowRight,
} from "react-icons/fi";
import Loader from "../../components/loader/loader";
import toast from "react-hot-toast";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";

const formatCurrency = (value) => {
    const number = Number(value || 0);
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(number);
};

const formatNumber = (value) => {
    const n = Number(value || 0);
    return new Intl.NumberFormat("en-IN").format(n);
};

const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const PLAN_COLORS = ["#6366F1", "#EC4899", "#22C55E", "#F97316", "#06B6D4", "#8B5CF6"];

const AiListingAnalytics = () => {
    const navigate = useNavigate();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const getDefaultDates = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30);
        return {
            startDate: startDate.toISOString().split("T")[0],
            endDate: endDate.toISOString().split("T")[0],
        };
    };

    const [filters, setFilters] = useState(getDefaultDates());
    const getTodayDate = () => new Date().toISOString().split("T")[0];

    const fetchAnalytics = async (isRefresh = false, overrideFilters) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        const effective = overrideFilters || filters;
        const requestBody = {
            startDate: effective.startDate,
            endDate: effective.endDate,
        };

        try {
            await new Promise((resolve, reject) => {
                getAiListingAnalytics(requestBody)((response) => {
                    if (response?.statusCode === 200) {
                        setAnalytics(response?.data);
                        const start = new Date(effective.startDate);
                        const end = new Date(effective.endDate);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                        toast.success(`AI listing analytics fetched for ${diffDays} ${diffDays === 1 ? "day" : "days"}`);
                        resolve(response);
                    } else {
                        setAnalytics(null);
                        reject(new Error("Failed to fetch AI listing analytics"));
                    }
                });
            });
        } catch (error) {
            console.error("Error fetching AI listing analytics:", error);
            setAnalytics(null);
            toast.error("Failed to fetch AI listing analytics data");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (key, value) => {
        const today = getTodayDate();
        if (key === "startDate") {
            if (value > today) return toast.error("Start date cannot be in the future");
            if (filters.endDate && value > filters.endDate) return toast.error("Start date cannot be after end date");
        }
        if (key === "endDate") {
            if (value > today) return toast.error("End date cannot be in the future");
            if (filters.startDate && value < filters.startDate) return toast.error("End date cannot be before start date");
        }
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        const hasBothDates = filters.startDate && filters.endDate;
        const isDefaultState = JSON.stringify(filters) === JSON.stringify(getDefaultDates());
        if (hasBothDates && !isDefaultState) {
            const timer = setTimeout(() => fetchAnalytics(true), 500);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleClearFilters = () => {
        const defaults = getDefaultDates();
        setFilters(defaults);
        setTimeout(() => fetchAnalytics(true, defaults), 100);
    };

    const isFilterActive = () => {
        const defaults = getDefaultDates();
        return filters.startDate !== defaults.startDate || filters.endDate !== defaults.endDate;
    };

    const isLoadingInitial = loading && !refreshing;
    const hasNoData = !loading && !analytics;

    const revenue = analytics?.revenue || {};
    const subscribers = analytics?.subscribers || {};
    const usage = analytics?.usage || {};
    const cost = analytics?.cost || {};
    const recentPurchases = Array.isArray(analytics?.recentPurchases) ? analytics.recentPurchases : [];

    const revenueByType = [
        { name: "Subscription", value: Number(revenue?.byType?.subscription || 0) },
        { name: "Top-up", value: Number(revenue?.byType?.topup || 0) },
    ];

    const creditsBreakdown = [
        { name: "Subscription", value: Number(usage?.creditsConsumedInRange?.subscription || 0) },
        { name: "Wallet", value: Number(usage?.creditsConsumedInRange?.wallet || 0) },
    ];

    const dailyConsumption = Array.isArray(usage?.dailyConsumption) ? usage.dailyConsumption : [];

    const subscribersByPlan = Object.entries(subscribers?.byPlan || {}).map(([plan, count]) => ({
        name: plan.charAt(0).toUpperCase() + plan.slice(1),
        value: Number(count || 0),
    }));

    const summaryCards = [
        {
            title: "Revenue (In Range)",
            value: formatCurrency(revenue?.inRange),
            sub: `Today ${formatCurrency(revenue?.today)} · MTD ${formatCurrency(revenue?.mtd)}`,
            icon: <FiDollarSign />,
            color: "#22C55E",
            onClick: () => navigate("/ai-listing-transactions"),
        },
        {
            title: "Active Subscribers",
            value: formatNumber(subscribers?.activeTotal),
            sub: `New ${formatNumber(subscribers?.newInRange)} · Cancelled ${formatNumber(subscribers?.cancelledInRange)}`,
            icon: <FiUsers />,
            color: "#6366F1",
            onClick: () => navigate("/ai-listing-vendors"),
        },
        {
            title: "Listings (In Range)",
            value: formatNumber(usage?.listingsInRange),
            sub: `All-time ${formatNumber(usage?.listingsAllTime)} · Active vendors ${formatNumber(usage?.activeVendorsInRange)}`,
            icon: <FiPackage />,
            color: "#EC4899",
        },
        {
            title: "Gross Margin",
            value: `${Number(cost?.grossMarginPct || 0).toFixed(2)}%`,
            sub: `Avg cost ${formatCurrency(cost?.avgCostPerListingInr)} / listing`,
            icon: <FiTrendingUp />,
            color: "#F97316",
        },
    ];

    const revenueAllTime = Number(revenue?.allTime || 0);
    const geminiSpend = Number(cost?.geminiSpendInrEstimate || 0);
    const totalCredits =
        Number(usage?.creditsConsumedInRange?.subscription || 0) +
        Number(usage?.creditsConsumedInRange?.wallet || 0);

    return (
        <div className="min-vh-100 ai-listing-analytics-page">
            <style>{`
                .ai-listing-analytics-page h3 { font-size: 18px !important; }
                .ai-listing-analytics-page h4 { font-size: 18px !important; }
                .ai-listing-analytics-page h5 { font-size: 14px !important; }
                .ai-listing-analytics-page h6 { font-size: 12px !important; }
                .ai-listing-analytics-page p { font-size: 12px; }
                .ai-listing-analytics-page .small,
                .ai-listing-analytics-page small { font-size: 11px !important; }
                .ai-listing-analytics-page .badge { font-size: 10px !important; padding: 3px 7px !important; }
                .ai-listing-analytics-page .card-body { padding: 12px !important; }
                .ai-listing-analytics-page .btn { font-size: 12px; }
                .ai-listing-analytics-page .recharts-text { font-size: 10px !important; }
                .ai-listing-analytics-page .table { font-size: 12px; margin-bottom: 0; }
                .ai-listing-analytics-page .table th { font-size: 10px !important; font-weight: 600; padding: 8px !important; letter-spacing: 0.04em; text-transform: uppercase; color: #64748b; }
                .ai-listing-analytics-page .table td { padding: 8px !important; vertical-align: middle; }
                .ai-listing-analytics-page .row { margin-bottom: 16px !important; margin-top: 0 !important; }
                .ai-listing-analytics-page .mb-2,
                .ai-listing-analytics-page .mb-3,
                .ai-listing-analytics-page .mb-4,
                .ai-listing-analytics-page .my-2,
                .ai-listing-analytics-page .mt-1 { margin-bottom: 16px !important; margin-top: 0 !important; }
                .ai-listing-analytics-page .card { margin-bottom: 0 !important; }

                /* Modern filter row (matches homepage) */
                .ai-listing-analytics-page .filters-row {
                    display: flex !important;
                    flex-wrap: nowrap !important;
                    align-items: center;
                    gap: 8px;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: none;
                    padding: 4px 2px 6px;
                }
                .ai-listing-analytics-page .filters-row::-webkit-scrollbar { display: none; }
                .ai-listing-analytics-page .filters-row > * { flex-shrink: 0 !important; }
                .ai-listing-analytics-page .filters-to { white-space: nowrap; color: #94a3b8; padding: 0 2px; font-size: 12px; }
                @media (min-width: 768px) { .ai-listing-analytics-page .filters-row { gap: 12px; } }

                .ai-listing-analytics-page .modern-date {
                    display: inline-block !important;
                    border: 1px solid #e2e8f0 !important;
                    border-radius: 8px !important;
                    padding: 5px 8px !important;
                    width: 135px !important;
                    height: 34px !important;
                    background-color: #fff !important;
                    color: #0f172a !important;
                    margin: 0 !important;
                    font-family: inherit;
                    font-size: 12px !important;
                }
                .ai-listing-analytics-page .modern-date:focus {
                    border-color: #008695 !important;
                    box-shadow: 0 0 0 3px rgba(0,134,149,0.15) !important;
                    outline: none;
                }
                @media (max-width: 576px) {
                    .ai-listing-analytics-page .modern-date { width: 120px !important; padding: 5px 6px !important; }
                }
                .ai-listing-analytics-page .modern-refresh {
                    border: 1px solid #e2e8f0 !important;
                    background: #fff !important;
                    color: #475569 !important;
                    border-radius: 8px !important;
                    padding: 6px 12px !important;
                    font-weight: 500 !important;
                    height: 34px !important;
                    white-space: nowrap;
                    font-size: 12px !important;
                    transition: all 0.15s ease;
                }
                .ai-listing-analytics-page .modern-refresh:hover {
                    border-color: #008695 !important;
                    color: #008695 !important;
                    background: rgba(0,134,149,0.06) !important;
                }
                .ai-listing-analytics-page .filters-clear {
                    height: 34px !important;
                    border-radius: 8px !important;
                    white-space: nowrap;
                    font-size: 12px !important;
                }

                @media (max-width: 767.98px) {
                    .ai-listing-analytics-page .page-header-sticky { flex-wrap: wrap !important; gap: 10px; }
                    .ai-listing-analytics-page .page-header-sticky > * { width: 100%; }
                    .ai-listing-analytics-page .overflow-auto::-webkit-scrollbar { display: none; }
                }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>

            {/* Header — uses shared .page-header-sticky */}
            <div className="page-header-sticky d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                    <div
                        className="back-btn"
                        onClick={() => navigate(-1)}
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '30px',
                            height: '30px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            background: '#fff',
                        }}
                    >
                        <IoMdArrowBack style={{ fontSize: '16px', color: '#475569' }} />
                    </div>
                    <h4 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.01em' }}>AI Listing Analytics</h4>
                </div>

                <div className="filters-row">
                    <input
                        type="date"
                        className="modern-date"
                        value={filters.startDate}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        max={filters.endDate || getTodayDate()}
                    />
                    <span className="filters-to">to</span>
                    <input
                        type="date"
                        className="modern-date"
                        value={filters.endDate}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        min={filters.startDate || undefined}
                        max={getTodayDate()}
                    />
                    {isFilterActive() && (
                        <button
                            onClick={handleClearFilters}
                            className="btn btn-sm btn-outline-secondary filters-clear"
                            disabled={refreshing}
                        >
                            Clear
                        </button>
                    )}
                    <button
                        onClick={() => fetchAnalytics(true)}
                        className="btn modern-refresh d-flex align-items-center gap-2"
                        disabled={refreshing}
                    >
                        <FiRefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                        <span>{refreshing ? 'Refreshing' : 'Refresh'}</span>
                    </button>
                </div>
            </div>

            {isLoadingInitial ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 280 }}>
                    <Loader />
                </div>
            ) : hasNoData ? (
                <div className="d-flex flex-column align-items-center justify-content-center text-center text-muted" style={{ minHeight: 280 }}>
                    <FiInfo size={32} className="mb-3 text-secondary" />
                    <h5 className="fw-bold mb-2">No Data Available</h5>
                    <p className="mb-0" style={{ maxWidth: 420 }}>
                        We couldn't find any AI listing analytics for the selected period.
                    </p>
                </div>
            ) : (
              <>
            {/* Summary Cards */}
            <div className="row g-3">
                {summaryCards.map((card, i) => {
                    const clickable = typeof card.onClick === "function";
                    return (
                        <div key={i} className="col-md-3 col-6">
                            <div
                                onClick={clickable ? card.onClick : undefined}
                                style={{
                                    background: '#fff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '10px',
                                    padding: '14px',
                                    cursor: clickable ? 'pointer' : 'default',
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                    height: '100%',
                                }}
                                onMouseEnter={
                                    clickable
                                        ? (e) => {
                                              e.currentTarget.style.transform = 'translateY(-2px)';
                                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.08)';
                                          }
                                        : undefined
                                }
                                onMouseLeave={
                                    clickable
                                        ? (e) => {
                                              e.currentTarget.style.transform = 'translateY(0)';
                                              e.currentTarget.style.boxShadow = 'none';
                                          }
                                        : undefined
                                }
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: 500, color: '#64748b' }}>{card.title}</div>
                                    <div
                                        style={{
                                            width: '30px', height: '30px',
                                            borderRadius: '8px',
                                            background: `${card.color}15`,
                                            color: card.color,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {React.cloneElement(card.icon, { size: 14 })}
                                    </div>
                                </div>
                                <div style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', lineHeight: 1.1, marginBottom: '4px' }}>
                                    {card.value}
                                </div>
                                <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>{card.sub}</div>
                                {clickable && (
                                    <div style={{ position: 'absolute', bottom: '10px', right: '10px', color: card.color, fontSize: '10px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '2px' }}>
                                        View <FiArrowRight size={10} />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Revenue Breakdown */}
            <div className="row g-3">
                <div className="col-lg-8">
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', height: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                            <h5 className="fw-bold" style={{ margin: 0 }}>Revenue Breakdown</h5>
                            <span style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '3px 8px', borderRadius: '999px' }}>
                                All time: {formatCurrency(revenueAllTime)}
                            </span>
                        </div>

                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px', height: 260 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={revenueByType}
                                    margin={{ top: 24, right: 12, left: 0, bottom: 8 }}
                                    barCategoryGap="30%"
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" tick={{ fontSize: 10, fontWeight: 600 }} />
                                    <YAxis tick={{ fontSize: 10, fontWeight: 600 }} tickFormatter={(v) => formatCurrency(v)} />
                                    <Tooltip cursor={{ fill: "transparent" }} formatter={(v) => formatCurrency(v)} />
                                    <Bar dataKey="value" name="Revenue" fill="#008695" radius={[6, 6, 0, 0]} barSize={48}>
                                        <LabelList
                                            dataKey="value"
                                            position="top"
                                            formatter={(v) => formatCurrency(v)}
                                            style={{ fontSize: 10, fontWeight: 700, fill: "#111827" }}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                            <span style={{ fontSize: '10px', fontWeight: 500, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '3px 8px', borderRadius: '999px' }}>
                                Refunds: {formatNumber(revenue?.refundsInRangeCount)}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 500, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '3px 8px', borderRadius: '999px' }}>
                                Subscription: {formatCurrency(revenue?.byType?.subscription)}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 500, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '3px 8px', borderRadius: '999px' }}>
                                Top-up: {formatCurrency(revenue?.byType?.topup)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Subscribers by Plan */}
                <div className="col-lg-4">
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', height: '100%' }}>
                        <h5 className="fw-bold" style={{ margin: '0 0 10px' }}>Subscribers by Plan</h5>

                        {subscribersByPlan.length > 0 && subscribersByPlan.some((p) => p.value > 0) ? (
                            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px', height: 260, position: 'relative' }}>
                                <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end' }}>
                                    {subscribersByPlan.map((p, i) => (
                                        <div
                                            key={p.name}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '5px',
                                                padding: '3px 7px', borderRadius: '6px',
                                                fontSize: 10,
                                                background: 'rgba(255,255,255,0.9)',
                                                border: '1px solid #eef2f7',
                                            }}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: PLAN_COLORS[i % PLAN_COLORS.length] }} />
                                            <span style={{ fontWeight: 500, color: '#64748b' }}>{p.name}</span>
                                            <span style={{ fontWeight: 700, color: '#0f172a' }}>{formatNumber(p.value)}</span>
                                        </div>
                                    ))}
                                </div>

                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={subscribersByPlan}
                                            dataKey="value"
                                            nameKey="name"
                                            innerRadius={40}
                                            outerRadius={75}
                                            paddingAngle={3}
                                        >
                                            {subscribersByPlan.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={PLAN_COLORS[index % PLAN_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <p style={{ color: '#94a3b8', margin: 0 }}>No plan-level subscribers in this period.</p>
                        )}

                        <div style={{ marginTop: '10px', fontSize: '10px', color: '#94a3b8' }}>
                            Pending scheduled: <strong style={{ color: '#0f172a' }}>{formatNumber(subscribers?.pendingScheduled)}</strong>
                        </div>
                    </div>
                </div>

                {/* Credits Consumed — full-width line chart */}
                <div className="col-12">
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                            <h5 className="fw-bold" style={{ margin: 0 }}>Credits Consumed Over Time</h5>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                <span style={{ fontSize: '10px', fontWeight: 500, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '3px 8px', borderRadius: '999px' }}>
                                    Subscription: {formatNumber(creditsBreakdown[0].value)}
                                </span>
                                <span style={{ fontSize: '10px', fontWeight: 500, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '3px 8px', borderRadius: '999px' }}>
                                    Wallet: {formatNumber(creditsBreakdown[1].value)}
                                </span>
                                <span style={{ fontSize: '10px', fontWeight: 500, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '3px 8px', borderRadius: '999px' }}>
                                    Total: {formatNumber(totalCredits)}
                                </span>
                            </div>
                        </div>

                        {dailyConsumption.length > 0 ? (
                            <div style={{ width: "100%", height: 240, background: '#f8fafc', borderRadius: '8px', padding: '10px 0' }}>
                                <ResponsiveContainer>
                                    <LineChart data={dailyConsumption} margin={{ top: 20, bottom: 30, right: 20, left: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="date"
                                            angle={-40}
                                            textAnchor="end"
                                            tick={{ fontSize: 10, fontWeight: 600 }}
                                            tickFormatter={(v) => new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                        />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fontWeight: 600 }} />
                                        <Tooltip
                                            content={({ active, payload, label }) => {
                                                if (active && payload && payload.length) {
                                                    return (
                                                        <div
                                                            style={{
                                                                background: "#fff",
                                                                border: "1px solid #e2e8f0",
                                                                borderRadius: '6px',
                                                                padding: '8px 10px',
                                                                fontSize: "11px",
                                                                lineHeight: "1.4",
                                                                boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
                                                            }}
                                                        >
                                                            <p style={{ fontWeight: 700, marginBottom: '4px', margin: '0 0 4px', fontSize: '11px' }}>{formatDate(label)}</p>
                                                            {payload.map((item, index) => (
                                                                <p key={index} style={{ color: item.stroke, margin: 0, fontSize: '11px' }}>
                                                                    {item.name}: <strong>{item.value}</strong>
                                                                </p>
                                                            ))}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Line type="monotone" dataKey="subscription" name="Subscription" stroke="#8B5CF6" strokeWidth={2} />
                                        <Line type="monotone" dataKey="wallet" name="Wallet" stroke="#EC4899" strokeWidth={2} />
                                        <Line type="monotone" dataKey="total" name="Total" stroke="#008695" strokeWidth={2} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <p style={{ color: '#94a3b8', margin: 0 }}>No daily consumption data for this period.</p>
                        )}
                    </div>
                </div>

                {/* Cost & Margin — full-width section */}
                <div className="col-12">
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' }}>
                        <h5 className="fw-bold" style={{ margin: '0 0 10px' }}>Cost & Margin</h5>

                        <div className="row g-3">
                            {[
                                {
                                    label: "Gemini Spend (Est.)",
                                    value: formatCurrency(geminiSpend),
                                    icon: <FiZap />,
                                    color: "#b45309",
                                    bg: "#fef3c7",
                                },
                                {
                                    label: "Avg Cost / Listing",
                                    value: formatCurrency(cost?.avgCostPerListingInr),
                                    icon: <FiCreditCard />,
                                    color: "#0369a1",
                                    bg: "#e0f2fe",
                                },
                                {
                                    label: "Gross Margin",
                                    value: `${Number(cost?.grossMarginPct || 0).toFixed(2)}%`,
                                    icon: <FiTrendingUp />,
                                    color: "#15803d",
                                    bg: "#dcfce7",
                                },
                                {
                                    label: "Listings Processed",
                                    value: formatNumber(usage?.listingsInRange),
                                    icon: <FiPackage />,
                                    color: "#6d28d9",
                                    bg: "#f5f3ff",
                                },
                            ].map((card, i) => (
                                <div className="col-md-3 col-6" key={i}>
                                    <div style={{
                                        background: card.bg,
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '10px',
                                        padding: '14px',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        minHeight: '110px',
                                    }}>
                                        <div style={{
                                            width: 32, height: 32,
                                            borderRadius: '8px',
                                            background: 'rgba(255,255,255,0.7)',
                                            color: card.color,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            marginBottom: '10px',
                                        }}>
                                            {React.cloneElement(card.icon, { size: 14 })}
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '11px', fontWeight: 500, color: '#64748b', marginBottom: '4px' }}>{card.label}</div>
                                            <div style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>{card.value}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {cost?.notes && (
                            <div style={{ marginTop: '10px', padding: '8px 10px', borderRadius: '6px', background: '#f8fafc', border: '1px solid #eef2f7' }}>
                                <div style={{ fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                    <FiInfo size={12} style={{ marginTop: '2px', flexShrink: 0 }} />
                                    <span>{cost.notes}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Recent Purchases — full-width section */}
                <div className="col-12">
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                            <h5 className="fw-bold" style={{ margin: 0 }}>Recent Purchases</h5>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '10px', fontWeight: 500, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '3px 8px', borderRadius: '999px' }}>
                                    {recentPurchases.length} {recentPurchases.length === 1 ? "transaction" : "transactions"}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => navigate("/ai-listing-transactions")}
                                    style={{
                                        border: '1px solid #008695',
                                        color: '#008695',
                                        background: '#fff',
                                        borderRadius: '8px',
                                        padding: '5px 10px',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    View all <FiArrowRight size={11} />
                                </button>
                            </div>
                        </div>

                        {recentPurchases.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table align-middle text-center">
                                    <thead style={{ background: "#f8fafc" }}>
                                        <tr>
                                            <th className="text-center">VENDOR</th>
                                            <th className="text-center">TYPE</th>
                                            <th className="text-center">ITEM</th>
                                            <th className="text-center">CREDITS</th>
                                            <th className="text-center">AMOUNT</th>
                                            <th className="text-center">DATE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentPurchases.map((purchase) => {
                                            const isSubscription = purchase.type === "subscription";
                                            return (
                                                <tr key={purchase.transactionId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td className="text-center">
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                                            {purchase.vendorImage ? (
                                                                <img
                                                                    src={purchase.vendorImage}
                                                                    alt={purchase.vendorName}
                                                                    style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: '50%', border: '1px solid #eef2f7', flexShrink: 0 }}
                                                                />
                                                            ) : (
                                                                <div style={{
                                                                    width: 24, height: 24,
                                                                    borderRadius: '50%',
                                                                    background: '#f1f5f9',
                                                                    color: '#64748b',
                                                                    fontSize: 10,
                                                                    fontWeight: 700,
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    flexShrink: 0,
                                                                }}>
                                                                    {(purchase.vendorName || "?").charAt(0).toUpperCase()}
                                                                </div>
                                                            )}
                                                            <span className="text-truncate" style={{ maxWidth: 140, fontWeight: 600, fontSize: '12px' }}>
                                                                {purchase.vendorName || "—"}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <span style={{
                                                            background: isSubscription ? '#ede9fe' : '#dbeafe',
                                                            color: isSubscription ? '#6d28d9' : '#1d4ed8',
                                                            fontWeight: 600,
                                                            fontSize: 10,
                                                            padding: '3px 8px',
                                                            borderRadius: '999px',
                                                            display: 'inline-block',
                                                        }}>
                                                            {isSubscription ? "Subscription" : "Top-up"}
                                                        </span>
                                                    </td>
                                                    <td className="text-center text-truncate" style={{ maxWidth: 140, fontSize: '12px' }} title={purchase.itemName}>
                                                        {purchase.itemName}
                                                    </td>
                                                    <td className="text-center" style={{ fontWeight: 600, fontSize: '12px' }}>
                                                        {formatNumber(purchase.creditsPurchased)}
                                                    </td>
                                                    <td className="text-center" style={{ fontWeight: 700, color: '#008695', fontSize: '12px' }}>
                                                        {formatCurrency(purchase.amountInr)}
                                                    </td>
                                                    <td className="text-center" style={{ color: '#94a3b8', fontSize: '11px' }}>
                                                        {formatDate(purchase.createdAt)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p style={{ color: '#94a3b8', margin: 0 }}>No recent purchases in this period.</p>
                        )}
                    </div>
                </div>
            </div>
              </>
            )}
        </div>
    );
};

export default AiListingAnalytics;
